import { Checkbox, Text, Tooltip } from "@vericus/cadmus-ui";

import { createColumnHelper } from "@tanstack/table-core";

import { IssueStatus, IssueType } from "@/generated/graphql";

import { ColumnId, DateRange, ProjectIssueWithChildren } from "../../types";
import { IssueDetailsTooltip } from "../issue";
import { AssigneeDropdown } from "../issue/assignee-dropdown";
import { IssueMenu } from "../issue/issue-menu-dropdown";
import { StatusDropdownWithRowData } from "../issue/status-dropdown";

const columnHelper = createColumnHelper<ProjectIssueWithChildren>();

// selection box
const selectionColumnDef = columnHelper.display({
  id: ColumnId.Select,
  size: 20,
  cell: ({ row }) => (
    <Checkbox
      checked={row.getIsSelected()}
      onChange={row.getToggleSelectedHandler()}
      aria-label="Select this row"
    />
  ),
});

// title
const titleColumnDef = columnHelper.accessor((row) => row.title, {
  id: ColumnId.Title,
  cell: ({ row }) => (
    <>
      <Tooltip
        content={<IssueDetailsTooltip data={row.original} />}
        side="bottom"
        align="start"
      >
        <Text
          kind="bodyLine"
          as="div"
          style={{
            maxWidth: "220px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {row.original.title}
        </Text>
      </Tooltip>
    </>
  ),
  filterFn: (row, columnId, filterValues: string[]) => {
    if (filterValues.length === 0) return true;

    const rowParent = row.getParentRow();
    const rowValue = row.getValue<string>(columnId);

    if (
      row.original.type === IssueType.Task &&
      filterValues.some(
        (filterValue) => rowParent?.original.title === filterValue
      )
    ) {
      return true;
    }

    // apply custom OR filtering logic here
    return filterValues.some((filterValue) => rowValue === filterValue);
  },
});

// description
const descriptionColumnDef = columnHelper.display({
  id: ColumnId.Description,
  cell: ({ row }) => (
    <Text
      kind="bodyLine"
      as="div"
      style={{
        maxWidth: "320px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {row.original.description}
    </Text>
  ),
});

// assignee
const assigneeColumnDef = columnHelper.accessor(
  (row) => row.assignees[0] ?? null,
  {
    id: ColumnId.Assignee,
    cell: ({ row }) => <AssigneeDropdown row={row} />,
    filterFn: (row, columnId, filterValues: string[]) => {
      if (filterValues.length === 0) return true;
      const rowValue = row.getValue<string>(columnId);

      // apply custom OR filtering logic here
      return filterValues.some((filterValue) => rowValue === filterValue);
    },
  }
);

// due date
const dueDateColumnDef = columnHelper.accessor((row) => row.dueDate, {
  id: ColumnId.DueDate,
  cell: ({ row }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
      }}
    >
      <Text kind="bodyMd" as="div">
        {row.original.dueDate
          ? new Date(row.original.dueDate).toDateString()
          : "No due date"}
      </Text>
    </div>
  ),
  filterFn: (row, _columnId, filterValue: DateRange) => {
    const [startDate, endDate] = filterValue;

    // If no filter is set, show all rows
    if (!startDate && !endDate) return true;

    // Handle rows with no due date
    if (!row.original.dueDate) return false;

    const rowDate = new Date(row.original.dueDate);

    // Check date range constraints
    return (
      (!startDate || rowDate >= startDate) && (!endDate || rowDate <= endDate)
    );
  },
});

// status
const statusColumnDef = columnHelper.accessor((row) => row.status, {
  id: ColumnId.Status,
  cell: ({ row }) => <StatusDropdownWithRowData row={row} />,
  filterFn: (row, columnId, filterValues: string[]) => {
    if (filterValues.length === 0) return true;

    // For task rows, check if their status matches the filter
    if (row.original.type === IssueType.Task) {
      return filterValues.includes(row.original.status);
    }

    // For milestone rows, check if any child tasks match the filter
    if (row.original.type === IssueType.Milestone) {
      const children = row.original.children || [];

      // If no children, hide milestone
      if (children.length === 0) return false;

      // Check all nested children recursively
      const hasMatchingChild = (items: ProjectIssueWithChildren[]): boolean => {
        return items.some((child) => {
          if (
            child.type === IssueType.Task &&
            filterValues.includes(child.status)
          ) {
            return true;
          }
          // Recursively check nested children if they exist
          if (child.children && child.children.length > 0) {
            return hasMatchingChild(child.children);
          }
          return false;
        });
      };

      return hasMatchingChild(children);
    }

    // Fallback
    const rowValue = row.getValue<IssueStatus>(columnId);
    return filterValues.includes(rowValue);
  },
  // Enable global filtering for this column
  enableGlobalFilter: true,
});

// actions col
const actionsColumnDef = columnHelper.display({
  id: ColumnId.Actions,
  cell: ({ row }) => <IssueMenu row={row} />,
});

// columns for the project issue table
// !! the order of columns here is explicit !!
export const PROJECT_ISSUE_COLUMNS = [
  selectionColumnDef,
  titleColumnDef,
  descriptionColumnDef,
  assigneeColumnDef,
  dueDateColumnDef,
  statusColumnDef,
  actionsColumnDef,
];
